import { useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent as ReactKeyboardEvent } from "react";
import type { AppController, AppSnapshot, DiaryEntry, ExecuteAction, FoodLogQuantity, FoodMeasure, MealName } from "../types";
import { MEAL_OPTIONS, mealLabel } from "../meals";

interface DiaryEntryEditorProps {
  snapshot: AppSnapshot;
  entry: DiaryEntry;
  controller: AppController;
  execute: ExecuteAction;
  onCancel: () => void;
  onSaved: () => void;
}

type QuantityKind = "amount" | "calories";

export function DiaryEntryEditor({ snapshot, entry, controller, execute, onCancel, onSaved }: DiaryEntryEditorProps) {
  const [date, setDate] = useState(entry.date);
  const [meal, setMeal] = useState<MealName>(entry.meal);
  const [kind, setKind] = useState<QuantityKind>("amount");
  const [measureId, setMeasureId] = useState<string | undefined>(() =>
    entry.measures?.some((measure) => measure.id === entry.portionDisplay?.measureId)
      ? entry.portionDisplay?.measureId
      : undefined,
  );
  const [value, setValue] = useState(() => formatInput(entry.portionDisplay?.amount ?? entry.portionAmount));
  const [saving, setSaving] = useState(false);
  const dialogRef = useRef<HTMLElement>(null);
  const allowedDays = useMemo(() => getAllowedDays(snapshot), [snapshot]);
  const numericValue = Number(value);
  const quantityValid = value.trim().length > 0 && Number.isFinite(numericValue) && numericValue > 0;
  const caloriesAvailable = entry.nutrition.calories > 0;
  const selectedMeasure = entry.measures?.find((measure) => measure.id === measureId);
  const preview = quantityValid ? calculatePreview(entry, kind, numericValue, selectedMeasure) : undefined;
  const nativeUnit = entry.measures && entry.measures.length > 0 ? "Amount" : unitLabel(entry);

  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialogRef.current?.querySelector<HTMLInputElement>('input[type="number"]')?.focus();
    return () => previous?.focus();
  }, []);

  function chooseKind(next: QuantityKind) {
    if (next === kind || (next === "calories" && !caloriesAvailable)) return;
    const current = quantityValid ? calculatePreview(entry, kind, numericValue, selectedMeasure) : undefined;
    setKind(next);
    setValue(formatConvertedInput(next === "amount"
      ? amountInMeasure(current?.amount ?? entry.portionAmount, selectedMeasure)
      : current?.calories ?? entry.nutrition.calories));
  }

  function chooseMeasure(nextId: string) {
    const current = quantityValid ? calculatePreview(entry, kind, numericValue, selectedMeasure) : undefined;
    const nextMeasure = entry.measures?.find((measure) => measure.id === nextId);
    setMeasureId(nextMeasure?.id);
    setKind("amount");
    setValue(formatConvertedInput(amountInMeasure(current?.amount ?? entry.portionAmount, nextMeasure)));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!quantityValid || saving) return;
    const quantity: FoodLogQuantity = kind === "amount"
      ? { kind, amount: numericValue, ...(selectedMeasure ? { measureId: selectedMeasure.id } : {}) }
      : { kind, calories: numericValue };
    setSaving(true);
    const result = await execute(
      () => controller.updateDiaryEntry({ entryId: entry.id, date, meal, quantity }),
      { successMessage: `${entry.foodName} updated.` },
    );
    setSaving(false);
    if (result.ok) onSaved();
  }

  function handleKeyDown(event: ReactKeyboardEvent<HTMLElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      if (!saving) onCancel();
      return;
    }
    if (event.key !== "Tab") return;
    const controls = Array.from(event.currentTarget.querySelectorAll<HTMLElement>(
      "input:not(:disabled), button:not(:disabled), select:not(:disabled)",
    )).filter((control) => control.tabIndex >= 0 && !control.hidden);
    if (controls.length === 0) return;
    const first = controls[0];
    const last = controls.at(-1)!;
    if (event.shiftKey && event.target === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && event.target === last) {
      event.preventDefault();
      first.focus();
    }
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <section
        ref={dialogRef}
        className="modal-sheet modal-sheet--quantity"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-diary-entry-heading"
        aria-busy={saving}
        onKeyDown={handleKeyDown}
      >
        <div className="modal-sheet__handle" aria-hidden="true" />
        <div className="modal-sheet__heading">
          <div>
            <p className="eyebrow">Food diary</p>
            <h2 id="edit-diary-entry-heading">Edit {entry.foodName}</h2>
            <p className="quantity-dialog__basis">Currently {entry.amountLabel} · {formatNumber(entry.nutrition.calories)} kcal</p>
          </div>
          <button className="icon-button" type="button" onClick={onCancel} aria-label="Close diary entry editor" disabled={saving}>x</button>
        </div>

        <form className="stack" onSubmit={submit}>
          <div className="compact-form-grid">
            <label className="field">
              <span>Date</span>
              <select value={date} onChange={(event) => setDate(event.target.value)} disabled={saving}>
                {allowedDays.map((day) => <option key={day.date} value={day.date}>{day.longLabel}</option>)}
              </select>
            </label>
            <label className="field">
              <span>Meal</span>
              <select value={meal} onChange={(event) => setMeal(event.target.value as MealName)} disabled={saving}>
                {MEAL_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
          </div>

          <fieldset className="fieldset quantity-picker" disabled={saving}>
            <legend>Amount eaten</legend>
            <div className="segmented-control segmented-control--two quantity-picker__modes">
              <label className={kind === "amount" ? "segmented-control__item segmented-control__item--selected" : "segmented-control__item"}>
                <input type="radio" name="edit-entry-kind" checked={kind === "amount"} onChange={() => chooseKind("amount")} />
                <span>{nativeUnit}</span>
              </label>
              <label className={`segmented-control__item${kind === "calories" ? " segmented-control__item--selected" : ""}${caloriesAvailable ? "" : " segmented-control__item--disabled"}`}>
                <input type="radio" name="edit-entry-kind" checked={kind === "calories"} disabled={!caloriesAvailable} onChange={() => chooseKind("calories")} />
                <span>Calories</span>
              </label>
            </div>
            {kind === "amount" && entry.measures && entry.measures.length > 0 && (
              <label className="field">
                <span>Unit</span>
                <select value={measureId ?? ""} aria-label="Food unit" onChange={(event) => chooseMeasure(event.target.value)}>
                  <option value="">{unitLabel(entry)}</option>
                  {entry.measures.map((measure) => (
                    <option key={measure.id} value={measure.id}>{measureOptionLabel(measure, entry)}</option>
                  ))}
                </select>
              </label>
            )}
            <label className="field">
              <span>{kind === "amount" ? `Quantity in ${selectedMeasure ? measureInputName(selectedMeasure) : unitLabel(entry).toLocaleLowerCase()}` : "Calories"}</span>
              <span className="input-with-suffix input-with-suffix--large">
                <input
                  type="number"
                  inputMode="decimal"
                  min="0.01"
                  step="any"
                  required
                  value={value}
                  aria-label={kind === "amount" ? `Quantity in ${selectedMeasure ? measureInputName(selectedMeasure) : unitLabel(entry).toLocaleLowerCase()}` : "Calories"}
                  aria-invalid={!quantityValid}
                  onChange={(event) => setValue(event.target.value)}
                />
                <i>{kind === "amount" ? measureSuffix(selectedMeasure) ?? unitSuffix(entry) : "kcal"}</i>
              </span>
            </label>
            {!quantityValid ? (
              <p className="form-error" role="alert">Enter a quantity greater than zero.</p>
            ) : preview ? (
              <div className="quantity-preview" role="status" aria-live="polite" aria-label="Updated entry preview">
                <dl className="quantity-preview__totals">
                  <div><dt>Amount</dt><dd>{formatPreviewAmount(
                    preview.amount,
                    numericValue,
                    entry,
                    kind === "amount" ? selectedMeasure : undefined,
                  )}</dd></div>
                  <div><dt>Calories</dt><dd>{formatNumber(preview.calories)} kcal</dd></div>
                </dl>
                <div className="macro-summary" aria-label="Updated nutrients">
                  <span>P {formatGrams(preview.protein)}</span>
                  <span>C {formatGrams(preview.carbohydrate)}</span>
                  <span>F {formatGrams(preview.fat)}</span>
                </div>
              </div>
            ) : null}
          </fieldset>

          <div className="modal-actions">
            <button className="button button--ghost" type="button" onClick={onCancel} disabled={saving}>Cancel</button>
            <button className="button button--primary" type="submit" disabled={saving || !quantityValid}>
              {saving ? "Saving…" : `Save ${mealLabel(meal)}`}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function getAllowedDays(snapshot: AppSnapshot) {
  if (!snapshot.checkIn.isLoggingLocked) return snapshot.week.days.filter((day) => !day.isFuture);
  const allowed = new Set(snapshot.checkIn.allowedBackfillDates);
  return snapshot.week.days.filter((day) => allowed.has(day.date));
}

function calculatePreview(entry: DiaryEntry, kind: QuantityKind, value: number, measure?: FoodMeasure) {
  const canonicalAmount = kind === "amount" ? value * (measure?.amount ?? 1) : undefined;
  const scale = kind === "amount" ? canonicalAmount! / entry.portionAmount : value / entry.nutrition.calories;
  if (!Number.isFinite(scale) || scale <= 0) return undefined;
  return {
    amount: entry.portionAmount * scale,
    calories: entry.nutrition.calories * scale,
    protein: entry.nutrition.proteinGrams * scale,
    carbohydrate: entry.nutrition.carbohydrateGrams * scale,
    fat: entry.nutrition.fatGrams * scale,
  };
}

function amountInMeasure(canonicalAmount: number, measure?: FoodMeasure): number {
  return measure ? canonicalAmount / measure.amount : canonicalAmount;
}

function measureInputName(measure: FoodMeasure): string {
  if (measure.id === "serving") return "servings";
  if (measure.id === "package") return "packages";
  return measure.label.toLocaleLowerCase();
}

function measureSuffix(measure?: FoodMeasure): string | undefined {
  if (!measure) return undefined;
  if (measure.id === "serving") return "servings";
  if (measure.id === "package") return "packages";
  return measure.label;
}

function measureOptionLabel(measure: FoodMeasure, entry: DiaryEntry): string {
  const conversion = `${formatNumber(measure.amount)} ${unitSuffix(entry)}`;
  return `${measure.label} · ${conversion}`;
}

function formatPreviewAmount(
  canonicalAmount: number,
  enteredAmount: number,
  entry: DiaryEntry,
  measure?: FoodMeasure,
): string {
  const canonical = `${formatNumber(canonicalAmount)} ${unitSuffix(entry)}`;
  if (!measure) return canonical;
  return `${formatNumber(enteredAmount)} ${measureSuffix(measure)} · ${canonical}`;
}

function unitLabel(entry: DiaryEntry): string {
  if (entry.basisUnit === "g") return "Grams";
  if (entry.basisUnit === "ml") return "Millilitres";
  return entry.servingName || "Servings";
}

function unitSuffix(entry: DiaryEntry): string {
  if (entry.basisUnit === "g" || entry.basisUnit === "ml") return entry.basisUnit;
  return entry.servingName || "serving";
}

function formatInput(value: number): string {
  return Number.isFinite(value) ? String(value) : "";
}

function formatConvertedInput(value: number): string {
  return Number.isFinite(value) ? String(Number(value.toPrecision(16))) : "";
}

function formatNumber(value: number): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatGrams(value: number): string {
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} g`;
}
